import {
  DeleteIcon,
  Group,
  InfiniteTable,
  InputDecimal,
} from '@/components/_common';
import { Col, Form, Modal, Popover, Row, Space, notification } from 'antd';
import { getTableScrollX } from '@/utils/table';
import { ColumnsType, ExpandableConfig } from 'antd/es/table/interface';
import useConfigurationColumnsPart from '@/hooks/useConfigurationColumnsPart';
import {
  KitCreateFormValues,
  KitItemCreate,
  KitItemType,
  kitTypeColor,
  kitTypeItem,
} from '@/core/domain/operational/Kits/Kits.types';
import StatusTag from '@/components/_common/StatusTag';
import { useIntl } from 'react-intl';
import { useKitItemsContext } from '@/pages/OperationalPages/KitsPage/KitsCreatePage';
import KitItemCompaniesList from './KitItemCompaniesList';
import { useKitItemsContextEdit } from '@/pages/OperationalPages/KitsPage/KitDetailPage';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import Authorities from '@/types/Authority';
import ActionIcon from '@/components/_common/ActionIcon';
import EllipsisHoverToggle from '@/components/_common/EllipsisHoverToggle';
import { ExternalLink } from 'react-feather';

interface Props {
  isEdit?: boolean;
}

const KitsItemsList: React.FC<Props> = ({ isEdit = false }) => {
  const intl = useIntl();
  const { items, setItems } = isEdit
    ? useKitItemsContextEdit()
    : useKitItemsContext();

  const form = Form.useFormInstance<KitCreateFormValues>();

  const increaseDecreaseGlobal = Form.useWatch('increaseDecrease', form);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  function changeFieldItem(key: [number, keyof KitItemCreate], value: any) {
    setItems((state) =>
      state.map((item, index) =>
        key[0] === index ? { ...item, [key[1]]: value } : item,
      ),
    );
  }

  function handleSetDiscountIncreaseInCompanies(index: number, value?: number) {
    setItems((state) =>
      state.map((item, itemIndex) =>
        itemIndex === index
          ? {
              ...item,
              companies: item.companies.map((company) => ({
                ...company,
                increaseDecrease: typeof value === 'number' ? value : undefined,
              })),
            }
          : item,
      ),
    );
  }

  function confirmRemoveItem(item: KitItemCreate, index: number) {
    if (isEdit) {
      setItems((prev) => prev.filter((_, i) => i !== index));
      notification.info({
        message: 'O item será removido ao ser salvo.',
      });
    } else {
      Modal.confirm({
        width: 600,
        title: 'Remoção',
        content: `Deseja realmente remover o item "${item.description}" do kit?`,
        onOk: () => {
          setItems((prev) => prev.filter((_, i) => i !== index));

          notification.success({
            message: 'Item removido com sucesso!',
          });
        },
        okType: 'danger',
        okText: 'Remover',
      });
    }
  }

  function redirectToItem(item: KitItemCreate) {
    if (item.itemType === KitItemType.part) {
      window.open(
        `/app/operational/inventory/parts/${item.itemId}/general`,
        '_blank',
      );
    } else {
      window.open(`/app/operational/tasks/${item.itemId}/general`, '_blank');
    }
  }

  const columns: ColumnsType<KitItemCreate> = [
    {
      key: 'description',
      dataIndex: 'description',
      title: 'Descrição',
      ellipsis: true,
      width: 200,
      render: (description: string, item) => (
        <Group gap={16} style={{ marginLeft: 4, lineHeight: 1 }}>
          <Popover
            content={
              item.itemType === KitItemType.part
                ? 'Visualizar peça'
                : 'Visualizar serviço'
            }
          >
            <ActionIcon onClick={() => redirectToItem(item)}>
              <ExternalLink size={18} />
            </ActionIcon>
          </Popover>

          <EllipsisHoverToggle>{description}</EllipsisHoverToggle>
        </Group>
      ),
    },
    {
      dataIndex: 'itemType',
      title: 'Tipo',
      width: 70,
      render: (_, row) => (
        <StatusTag
          color={kitTypeColor[row.itemType]}
          label={kitTypeItem[row.itemType]}
        />
      ),
    },
    useConfigurationColumnsPart({
      isSorter: false,
      isSku: true,
      widthColumns: 90,
    }),
    {
      key: 'manufacturer',
      dataIndex: ['manufacturer', 'name'],
      title: 'Fabricante',
      ellipsis: true,
      width: 110,
      render: (value) => value || '-',
    },
    {
      dataIndex: 'quantity',
      title: 'Qtde./Temp.',
      width: 80,
      ellipsis: true,
      render: (value, __, index) => (
        <InputDecimal
          value={value}
          onChange={(newValue) =>
            changeFieldItem([index, 'quantity'], newValue)
          }
        />
      ),
    },
    {
      dataIndex: 'increaseDecrease',
      title: 'Acréscimo/Decréscimo (%)',
      ellipsis: true,
      width: 100,
      render: (value, _, index) => (
        <InputDecimal
          value={increaseDecreaseGlobal ? increaseDecreaseGlobal : value}
          onChange={(newValue) => {
            changeFieldItem([index, 'increaseDecrease'], newValue);

            handleSetDiscountIncreaseInCompanies(index, newValue);
          }}
          disabled={
            typeof increaseDecreaseGlobal === 'number' ||
            !hasPermissions([Authorities.KITS_CHANGE_PRICES])
          }
        />
      ),
    },
    {
      key: 'actions',
      width: 50,
      fixed: 'right',
      align: 'right',
      title: intl.formatMessage({
        id: 'commons.list.header.action',
      }),
      render: (_, row, index) => (
        <Space>
          <DeleteIcon
            onClick={() => confirmRemoveItem(row, index)}
            disabled={isEdit && !hasPermissions([Authorities.KITS_DELETE])}
          />
        </Space>
      ),
    },
  ];

  const renderCompaniesExpandable: ExpandableConfig<KitItemCreate> = {
    columnWidth: 20,
    rowExpandable: (row) => !!row.companies?.length,
    expandedRowRender: (row) => (
      <KitItemCompaniesList
        isEdit={isEdit}
        companies={row.companies}
        kitItem={row}
      />
    ),
  };

  return (
    <>
      <Row gutter={[12, 12]}>
        <Col span={24}>
          <InfiniteTable
            dataSource={items}
            rowKey={(row) => row.itemId}
            columns={columns}
            scroll={{ x: getTableScrollX(columns) }}
            expandable={renderCompaniesExpandable}
            loading={false}
          />
        </Col>
      </Row>
    </>
  );
};

export default KitsItemsList;
